import { KlipyConfig } from './config';

// Raw API response format
interface KlipyApiGif {
    id: number;
    slug: string;
    title: string;
    file: {
        hd: KlipyFileFormat;
        md: KlipyFileFormat;
        sm: KlipyFileFormat;
    };
}

interface KlipyFileFormat {
    gif: { url: string; width: number; height: number; size: number };
    webp: { url: string; width: number; height: number; size: number };
    jpg: { url: string; width: number; height: number; size: number };
    mp4: { url: string; width: number; height: number; size: number };
}

interface KlipyApiResponse {
    result: boolean;
    data: {
        data: KlipyApiGif[];
        current_page?: number;
        per_page?: number;
        total?: number;
    };
}

// Normalized GIF format for component usage
export interface KlipyGif {
    slug: string;
    title: string;
    preview: string;
    gif: {
        url: string;
        width: number;
        height: number;
        size: number;
    };
}

export interface KlipySearchResponse {
    items: KlipyGif[];
    page: number;
    per_page: number;
    total?: number;
}

/**
 * Build the Klipy API URL with the app key
 */
function buildUrl(endpoint: string): string {
    return `${KlipyConfig.API_BASE}/v1/${KlipyConfig.API_KEY}${endpoint}`;
}

/**
 * Transform API response to normalized format
 */
function transformResponse(apiResponse: KlipyApiResponse): KlipySearchResponse {
    const items = apiResponse.data.data.map((gif): KlipyGif => ({
        slug: gif.slug,
        title: gif.title,
        preview: gif.file.sm.gif.url,
        gif: {
            url: gif.file.hd.gif.url,
            width: gif.file.hd.gif.width,
            height: gif.file.hd.gif.height,
            size: gif.file.hd.gif.size,
        },
    }));

    return {
        items,
        page: apiResponse.data.current_page || 1,
        per_page: apiResponse.data.per_page || items.length,
        total: apiResponse.data.total,
    };
}

/**
 * Search for GIFs using Klipy API
 */
export async function searchGifs(
    query: string,
    limit: number = 20,
    page: number = 1
): Promise<KlipySearchResponse> {
    const params = new URLSearchParams({
        q: query,
        per_page: limit.toString(),
        page: page.toString(),
    });

    const response = await fetch(`${buildUrl('/gifs/search')}?${params}`);

    if (!response.ok) {
        throw new Error(`Klipy API error: ${response.statusText}`);
    }

    const apiResponse: KlipyApiResponse = await response.json();
    return transformResponse(apiResponse);
}

/**
 * Get featured/trending GIFs
 */
export async function getFeaturedGifs(
    limit: number = 40,
    page: number = 1
): Promise<KlipySearchResponse> {
    const params = new URLSearchParams({
        per_page: limit.toString(),
        page: page.toString(),
    });

    const response = await fetch(`${buildUrl('/gifs/trending')}?${params}`);

    if (!response.ok) {
        throw new Error(`Klipy API error: ${response.statusText}`);
    }

    const apiResponse: KlipyApiResponse = await response.json();
    return transformResponse(apiResponse);
}

/**
 * Search for stickers using Klipy API
 */
export async function searchStickers(
    query: string,
    limit: number = 20,
    page: number = 1
): Promise<KlipySearchResponse> {
    const params = new URLSearchParams({
        q: query,
        per_page: limit.toString(),
        page: page.toString(),
    });

    const response = await fetch(`${buildUrl('/stickers/search')}?${params}`);

    if (!response.ok) {
        throw new Error(`Klipy API error: ${response.statusText}`);
    }

    const apiResponse: KlipyApiResponse = await response.json();
    return transformResponse(apiResponse);
}

/**
 * Get trending stickers
 */
export async function getTrendingStickers(
    limit: number = 40,
    page: number = 1
): Promise<KlipySearchResponse> {
    const params = new URLSearchParams({
        per_page: limit.toString(),
        page: page.toString(),
    });

    const response = await fetch(`${buildUrl('/stickers/trending')}?${params}`);

    if (!response.ok) {
        throw new Error(`Klipy API error: ${response.statusText}`);
    }

    const apiResponse: KlipyApiResponse = await response.json();
    return transformResponse(apiResponse);
}

/**
 * Register a share event (analytics)
 */
export async function registerShare(gifSlug: string): Promise<void> {
    await fetch(buildUrl(`/gifs/share/${gifSlug}`), {
        method: 'POST',
    });
}
